import { CopyIcon } from "@phosphor-icons/react/dist/csr/Copy";
import { useMemo } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { copyNoteGrid, createNoteGridPreview } from "./utils";

export const NoteGrid = ({
  text,
  copyable = false,
}: {
  text: string;
  copyable?: boolean;
}) => {
  const grid = useMemo(() => createNoteGridPreview(text), [text]);
  const copyCells = () => {
    void copyNoteGrid(text).then(
      () => toast.success("Cells copied"),
      () =>
        toast.error("Cells not copied. Allow clipboard access and try again."),
    );
  };
  return (
    <Stack gap={1} className="min-w-0">
      <Stack className="group/note-grid relative min-w-0">
        <Stack className="max-h-48 max-w-full min-w-0 overflow-auto rounded-md border border-line">
          <table
            className="w-max min-w-full border-collapse text-sm"
            aria-label="Pasted grid cells"
          >
            <tbody>
              {grid.previewRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, columnIndex) => (
                    <td
                      key={columnIndex}
                      className="max-w-80 min-w-20 border border-line px-2 py-1 align-top break-words whitespace-pre-wrap"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
        {copyable && (
          <Stack className="pointer-events-none absolute top-1.5 right-1.5 z-10 rounded-md bg-surface-raised opacity-0 shadow-sm transition-opacity group-focus-within/note-grid:pointer-events-auto group-focus-within/note-grid:opacity-100 group-hover/note-grid:pointer-events-auto group-hover/note-grid:opacity-100 pointer-coarse:pointer-events-auto pointer-coarse:opacity-100">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    size="icon-xs"
                    variant="outline"
                    aria-label="Copy cells"
                    onClick={copyCells}
                  />
                }
              >
                <CopyIcon aria-hidden />
              </TooltipTrigger>
              <TooltipContent side="top" align="end">
                Copy cells
              </TooltipContent>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      {grid.hasMoreCells && (
        <Text variant="caption" tone="muted">
          Preview: {grid.previewRows.length} of {grid.rowCount} rows,{" "}
          {grid.previewRows[0]?.length ?? 0} of {grid.columnCount} columns. Copy
          cells includes all cells.
        </Text>
      )}
    </Stack>
  );
};
